import { useLocalStorage } from "@uidotdev/usehooks";
import { Dispatch, SetStateAction, useCallback, useState } from "react";

export interface CheckableItem {
  name: string;
  isChecked: boolean;
}
export type ToggleCheck = (name: string) => void;

export interface SelectionState {
  items: CheckableItem[];
  toggleCheck: ToggleCheck;
  setAllUnchecked: () => void;
  setAllChecked: () => void;
  setCheckedState: (checkedItemNames: string[]) => void;
}

function useSelectionStateInner(
  items: CheckableItem[],
  setItems: Dispatch<SetStateAction<CheckableItem[]>>,
): SelectionState {
  const toggleCheck = useCallback(
    (itemName: string) =>
      setItems((items) =>
        items.map(({ name, isChecked }) =>
          name === itemName ? { name, isChecked: !isChecked } : { name, isChecked },
        ),
      ),
    [setItems],
  );

  const setAllChecked = useCallback(
    () => setItems((items) => items.map(({ name }) => ({ name, isChecked: true }))),
    [setItems],
  );

  const setAllUnchecked = useCallback(
    () => setItems((items) => items.map(({ name }) => ({ name, isChecked: false }))),
    [setItems],
  );

  const setCheckedState = useCallback(
    (checkedItemNames: string[]) =>
      setItems((items) =>
        items.map(({ name }) => ({ name, isChecked: checkedItemNames.includes(name) })),
      ),
    [setItems],
  );

  return { items, toggleCheck, setAllChecked, setAllUnchecked, setCheckedState };
}

export function useSelectionState(initialState: CheckableItem[]): SelectionState {
  const [items, setItems] = useState(initialState);
  return useSelectionStateInner(items, setItems);
}

export function usePersistedSelectionState(
  persistKey: string,
  initialState: CheckableItem[],
): SelectionState {
  const [items, setItems] = useLocalStorage(persistKey, initialState);
  if (!items || items.length === 0) {
    // If the local storage is empty, set it to the initial state
    setItems(initialState);
  }
  return useSelectionStateInner(items, setItems);
}
